'use client';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import type { ProfileUser } from '@lingugeek/schemas';
import { Menu } from 'lucide-react';
import { UserIcon, ChevronDownIcon } from '@/components/icons';

interface DashboardNavbarProps {
  user?: ProfileUser;
  onLogout: () => void;
  onMenuToggle?: () => void;
}

export default function DashboardNavbar({
  user,
  onLogout,
  onMenuToggle,
}: DashboardNavbarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <header className='sticky top-0 z-50 flex items-center justify-between w-full px-4 py-4 shadow-sm bg-brand-teal'>
      <div className='flex items-center gap-3'>
        <button
          onClick={onMenuToggle}
          className='p-2 rounded-md hover:bg-brand-yellow/20 md:hidden'
        >
          <Menu className='w-6 h-6 text-brand-navy' />
        </button>
        <Link
          to='/'
          className='flex items-center gap-3 transition-opacity hover:opacity-80'
        >
          <Logo size={40} />
          <span className='text-2xl font-extrabold text-brand-navy'>
            LinguGeek
          </span>
        </Link>
      </div>

      {/* User Menu */}
      <div className='relative'>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className='flex items-center gap-3 p-2 transition-colors rounded-md hover:bg-brand-yellow/20'
        >
          <div className='flex items-center justify-center w-8 h-8 overflow-hidden rounded-full bg-brand-navy'>
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name || 'User'}
                className='object-cover w-full h-full'
              />
            ) : (
              <UserIcon className='w-5 h-5 text-brand-yellow' />
            )}
          </div>
          <span className='hidden font-medium text-brand-navy sm:block'>
            {user?.name || user?.username || 'User'}
          </span>
          <ChevronDownIcon
            className={`w-4 h-4 text-brand-navy transition-transform ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <>
            <div
              className='fixed inset-0 z-10'
              onClick={() => setIsDropdownOpen(false)}
            />
            <div className='absolute right-0 z-20 w-48 mt-2 bg-white border border-gray-200 rounded-md shadow-lg'>
              <div className='py-2'>
                <div className='px-4 py-2 border-b border-gray-100'>
                  <p className='text-sm font-medium text-brand-navy'>
                    {user?.name || user?.username}
                  </p>
                  <p className='text-xs text-gray-500'>{user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    onLogout();
                    setIsDropdownOpen(false);
                  }}
                  className='block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50'
                >
                  Sign Out
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
